import { useMemo } from "react";

import { getTzDayjs } from "@m7-health/shared-utils";

import { useAppConfigQuery } from "#/features/User/queries";
import { IUnit, useListSchedulesQuery } from "@/api";
import { useCurrentTimezone, useMapBy, useToast } from "@/common/hooks";
import { getTzFormattedDate } from "@/common/utils/dates";

import { UnitPickerWrapper } from "../../UnitPicker/UnitPickerWrapper";
import { getMatchingIncentive } from "../helpers";
import { TShiftEditorProps } from "../types";

export const ShiftEditorUnitPicker = ({
  staffShift,
  updateStaffShift,
  unitOfShift,
  staffDetails,
}: TShiftEditorProps) => {
  const toast = useToast();

  // use timezone of unit of shift, NOT facility timezone
  const timezone = useCurrentTimezone(unitOfShift.id);
  const shiftDate = getTzFormattedDate(staffShift.date, timezone);

  // Unit selector
  const availableUnitsIds = useMapBy(staffDetails.user.rosters, "unitId");
  const allAvailableUnits = useAppConfigQuery().data?.accessibleUnits;
  const staffAvailableUnits = useMemo(
    () => allAvailableUnits?.filter(({ id }) => availableUnitsIds.includes(id)),
    [allAvailableUnits, availableUnitsIds],
  );
  const selectedUnit = allAvailableUnits?.find(({ id }) => id === unitOfShift.id);
  const { data: schedules } = useListSchedulesQuery(
    { unitIds: availableUnitsIds },
    { skip: availableUnitsIds.length === 0 },
  );

  return (
    <UnitPickerWrapper
      label="Unit"
      className="shift-editor-unit-picker"
      dropdownClassName="shift-editor-unit-picker-dropdown"
      unitOptions={staffAvailableUnits}
      onChange={(selectedUnits) => {
        // Since we're using it as a single-select, we'll take the first unit
        const newSelectedUnit = selectedUnits[0];
        if (!newSelectedUnit) return;
        const newlySelectedSchedule = (schedules || [])?.find(
          ({ unitId, startDay, endDay }) =>
            newSelectedUnit?.id === unitId && startDay <= shiftDate && shiftDate <= endDay,
        );
        if (!newlySelectedSchedule) {
          toast.showInfo(
            `There is no schedule for ${newSelectedUnit?.name || "this unit"} on ${getTzDayjs(shiftDate, timezone).format("MM/DD/YYYY")}, so this shift cannot be moved to it.`,
          );
          return;
        }
        updateStaffShift({
          ...staffShift,
          scheduleId: newlySelectedSchedule.id,
          attributes: [],
          incentiveLevelId: getMatchingIncentive({
            fromUnitId: unitOfShift.id,
            toUnitId: newSelectedUnit.id,
            unitsConfigs: allAvailableUnits || [],
            fromIncentiveLevelId: staffShift.incentiveLevelId,
          }),
          isWorkingAway: newSelectedUnit.id !== staffDetails.homeUnitId,
        });
      }}
      multiple={false} // Set to false for single-select behavior
      values={selectedUnit ? [selectedUnit as IUnit] : []} // Pass current unit as array
      size="small"
      showFullUnitNames
      homeUnitToShowFloatingIcons={staffDetails.homeUnitId}
      reversed
    />
  );
};
